import { useState } from "react";
import { CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "../lib/api";
import { useRouters } from "../context/RouterContext";
import { RouterCombobox } from "./RouterCombobox";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const sisa = (sec) => {
  if (sec === null || sec === undefined) return "-";
  const s = Math.abs(sec); const d = Math.floor(s / 86400); const h = Math.floor((s % 86400) / 3600); const m = Math.floor((s % 3600) / 60);
  const t = [d && `${d} hari`, h && `${h} jam`, (!d && m) && `${m} menit`].filter(Boolean).join(" ") || "< 1 menit";
  return sec <= 0 ? `habis ${t} lalu` : `sisa ${t}`;
};

export function CekMasaBerlakuDialog({ open, onClose, suggestions = [] }) {
  const { routers } = useRouters();
  const [code, setCode] = useState("");
  const [routerId, setRouterId] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const cek = async (c = code) => {
    if (!c.trim()) return;
    setLoading(true); setData(null);
    try { const { data } = await api.post("/vouchers/cek-masa-berlaku", { code: c.trim(), channel: "", router_id: routerId }); setData(data); }
    catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="dialog-cek-masa-berlaku">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><CalendarClock className="w-5 h-5 text-violet-400" /> Cek Masa Berlaku (Script Router)</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Dibaca langsung dari System → Scripts di router: waktu login pertama + validity (mis. 3h, 30d) = kedaluwarsa. Lebih akurat daripada perkiraan sistem.</p>
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); cek(); }}>
          <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Kode voucher" className="font-mono" autoFocus data-testid="cmb-input" />
          <Button type="submit" disabled={loading || !code.trim()} data-testid="cmb-submit">{loading ? "Membaca..." : "Cek"}</Button>
        </form>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground shrink-0">Cabang / Router:</span>
          <RouterCombobox routers={[...routers].sort((a, b) => a.name.localeCompare(b.name))} value={routerId || "ALL"} onChange={(v) => setRouterId(v === "ALL" ? "" : v)} allLabel="Otomatis (dari data voucher)" testid="cmb-router" />
        </div>
        {suggestions.length > 0 && (
          <div className="flex flex-wrap gap-1.5 text-xs" data-testid="cmb-suggestions">
            <span className="text-muted-foreground">Terdeteksi di chat:</span>
            {suggestions.map((s) => <button key={s} type="button" className="font-mono px-2 py-0.5 rounded border border-border hover:border-violet-400 hover:text-violet-400 transition-colors" onClick={() => { setCode(s); cek(s); }} data-testid={`cmb-sug-${s}`}>{s}</button>)}
          </div>
        )}
        {data?.results?.map((r) => (
          <div key={r.router_id} className="rounded-xl border border-border p-3 space-y-2 text-sm" data-testid={`cmb-result-${r.router_id}`}>
            <div className="flex items-center justify-between gap-2"><div className="font-semibold">{r.router_name}</div><div className="font-mono text-[10px] text-muted-foreground">jam router {r.router_time || "-"}</div></div>
            {r.error && <div className="text-rose-400 text-xs">Router tidak merespons: {r.error}</div>}
            {!r.error && !r.items.length && <div className="text-xs text-amber-300 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2" data-testid={`cmb-none-${r.router_id}`}>Tidak ada script untuk kode ini — voucher <b>belum pernah login</b> (masa berlaku belum mulai berjalan).</div>}
            {r.items.map((it, i) => (
              <div key={i} className={`rounded-lg border px-3 py-2 text-xs space-y-1 ${it.expired === null ? "border-border" : it.expired ? "border-rose-500/40 bg-rose-500/10" : "border-emerald-500/40 bg-emerald-500/10"}`} data-testid={`cmb-item-${r.router_id}-${i}`}>
                <div className="flex justify-between gap-2"><span className="font-mono font-bold">{it.code}</span><span className="font-mono">{it.profile || "-"} · {it.validity || "-"}</span></div>
                <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5">
                  <span className="text-muted-foreground">Login pertama</span><span className="font-mono text-right">{it.login_at ? fmtDateTime(it.login_at) : it.script_name.split("-|-")[0]}</span>
                  <span className="text-muted-foreground">Kedaluwarsa</span><span className="font-mono text-right">{it.expire_at ? fmtDateTime(it.expire_at) : "-"}</span>
                  <span className="text-muted-foreground">Status</span><span className={`font-mono font-bold text-right ${it.expired ? "text-rose-400" : "text-emerald-400"}`}>{it.expired === null ? "tidak bisa dihitung" : sisa(it.remaining_sec)}</span>
                  <span className="text-muted-foreground">IP / MAC</span><span className="font-mono text-right text-[10px]">{it.ip} · {it.mac}</span>
                </div>
              </div>
            ))}
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
}
